import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../../../api';
import { celebrar } from './celebracion';
import { trozosRosco, trozoSuelto } from './quesitos';
import './mi-motivacion-paciente.css';

/**
 * MiMotivacionPaciente — RÉPLICA de «Mi motivación» (PROMPT_PESTANA_MI_MOTIVACION.md).
 * El Reto (una sola motivación en curso) · El Test (cita, 5 preguntas, giro, premio
 * con celebración) · Mi constancia (calendario del mes con roscos, racha, %).
 * No se copia el código: se replica el comportamiento respetando el CSS de marca.
 *
 * DATOS demo hasta atar el origen real. Las LÁMINAS de reto no existen aún →
 * HUECO MARCADO (regla 6: nunca una foto prestada).
 */

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS_SEM = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

// El reto en curso (demo). meta=3 (objetivo), dia=5 (viernes = día del test).
const RETO = {
  objetivo: 'Volver a coger en brazos a mi nieta sin miedo.',
  niveles: [
    { rot: 'Todavía no llego', txt: 'De momento no puedo levantar el brazo del todo.' },
    { rot: 'Estoy cerca', txt: 'Ya llego, pero me cuesta y no aguanto mucho.' },
    { rot: 'Ya llego', txt: 'Consigo sujetar un peso ligero un ratito.' },
    { rot: 'Lo hago con facilidad', txt: 'Lo hago cómodamente, sin miedo.' },
  ],
  meta: 3,
  dia: 5,
};

const PREGUNTAS = [
  { k: 'capacidad', q: '¿Hasta dónde has llegado hoy?', tipo: 'niveles' },
  { k: 'esfuerzo', q: '¿Cuánto te ha costado?', ops: ['Nada', 'Poco', 'Bastante', 'Muchísimo'], nota: 'Si te cuesta poco, el siguiente nivel está más cerca de lo que crees.' },
  { k: 'dolor', q: '¿Te ha dolido al hacerlo?', ops: ['No', 'Un poco', 'Bastante', 'Mucho, tuve que parar'], nota: 'Esta no cambia tu resultado. Es para que lo sepa tu fisio.' },
  { k: 'pautas', q: 'Esta semana, ¿hiciste todas las pautas que pudiste?', ops: ['Sí, todas', 'Casi todas', 'Solo algunas', 'Casi ninguna'], nota: 'No hay respuesta mala. Saberlo es lo que permite ajustarte la pauta.' },
  { k: 'mejora', q: '¿Notas que vas mejorando?', ops: ['Sí, bastante', 'Un poco', 'Igual que antes', 'Peor'], nota: null },
];

// Estrellas (n llenas de `de`).
// Estrellas · ORO #E8B33A, el único color de fuera de la paleta (§3.8.7).
const estrellas = (n, de) => Array.from({ length: de || 4 }, (_, k) => (
  <svg key={k} className={'fv-estrella' + (k < n ? ' es-llena' : '')} viewBox="0 0 24 24">
    <path d="M12 2.6l2.7 6.4 6.9.5-5.3 4.5 1.7 6.7L12 17l-6 3.7 1.7-6.7L2.4 9.5l6.9-.5z" />
  </svg>
));

const HOY = new Date();

/* ══════════════════════════════════════════════════════════════════
   MI CONSTANCIA · DATO REAL
   ------------------------------------------------------------------
   Aquí había un `diarioDemo()` que se INVENTABA el mes entero con una
   fórmula: los roscos, la racha, el porcentaje, y hasta días marcados
   con dolor que la paciente nunca tuvo. Eso es un registro clínico
   fabricado en la pantalla de alguien que se lo cree — y es peor que
   un dato que falta, porque un hueco se ve y esto parecía verdad.

   Ahora sale de GET /api/paciente/pautas/calendario, que da:
       celdas[] { fecha, momento, idx, programado, hecho }
       dias[]   { fecha, es_hoy, es_futuro }

   ⚠️ UN DÍA = UN HUECO POR CADA EJERCICIO QUE TENÍA PROGRAMADO ESE
   DÍA (`programado`), no los que tiene hoy. La pauta cambia con el
   tratamiento: si a mayo le pusiéramos los ejercicios de hoy, el mes
   saldría PEOR de lo que fue. Y no daría ningún error.

   ⚠️ Solo hay dos estados reales: hecho y no hecho. El servidor no
   guarda «a medias» ni «me dolía», así que aquí NO se inventan.
   ══════════════════════════════════════════════════════════════════ */
function diarioDeCeldas(datos) {
  const d = {};
  if (!datos || !Array.isArray(datos.celdas)) return d;
  const futuros = new Set((datos.dias || []).filter((x) => x.es_futuro).map((x) => x.fecha));
  datos.celdas.forEach((c) => {
    if (!c.programado) return;              // ese día no le tocaba: no cuenta
    if (futuros.has(c.fecha)) return;       // un día que no ha llegado NO lleva rosco
    const [ay, am, ad] = String(c.fecha).split('-').map(Number);
    if (!ay) return;
    const k = `${ay}-${am - 1}-${ad}`;      // el mes, en base 0, como el calendario
    // el matiz, no solo sí/no · 'dolor' manda: es lo que el fisio mira
    const est = c.estado === 'dolor' ? 'dolor'
      : c.estado === 'media' ? 'medias'
      : c.estado === 'parada' ? 'no'
      : (c.hecho ? 'si' : 'no');
    (d[k] = d[k] || []).push(est);
  });
  return d;
}

/* QUIÉN es cada trozo del rosco · mismo orden que el diario.
   El panel del día pintaba «Ejercicio 1 · Ejercicio 2…» con un hueco gris,
   sin mirar el dato. Así la paciente ve cinco casillas iguales y no sabe cuál
   hizo; y el nombre y la lámina estaban ahí, en `filas`. */
function detalleDeCeldas(datos) {
  const d = {};
  if (!datos || !Array.isArray(datos.celdas)) return d;
  const info = {};
  (datos.filas || []).forEach((f) => { info[`${f.momento}|${f.idx}`] = f; });
  const futuros = new Set((datos.dias || []).filter((x) => x.es_futuro).map((x) => x.fecha));
  datos.celdas.forEach((c) => {
    if (!c.programado || futuros.has(c.fecha)) return;
    const [ay, am, ad] = String(c.fecha).split('-').map(Number);
    if (!ay) return;
    const f = info[`${c.momento}|${c.idx}`] || {};
    (d[`${ay}-${am - 1}-${ad}`] = d[`${ay}-${am - 1}-${ad}`] || [])
      .push({ titulo: f.titulo || '', foto: f.foto || '' });
  });
  return d;
}
/* LO QUE TE TOCA UN DÍA · del calendario REAL, no de un horario inventado.
   Aquí había unas horas fijas escritas a mano (09:30 · 18:00) con campanita de
   aviso incluida. Eso le prometía a la paciente un recordatorio que nadie iba a
   mandarle. El servidor no guarda horas; lo que sí sabe es QUÉ EJERCICIOS le
   tocan y en qué momento del día, así que es lo que se enseña. */
function planDeFecha(datos, fechaISO) {
  if (!datos || !Array.isArray(datos.celdas)) return [];
  const nombre = {};
  (datos.filas || []).forEach((f) => { nombre[f.momento] = f.momento_nombre || f.momento; });
  const cuenta = {};
  datos.celdas.forEach((c) => {
    if (c.fecha !== fechaISO || !c.programado) return;
    cuenta[c.momento] = (cuenta[c.momento] || 0) + 1;
  });
  return Object.keys(cuenta).map((m) => ({ mom: nombre[m] || m, n: cuenta[m] }));
}
// resumen de un día (todo|parte|parada|no) a partir de sus trozos.
const resumenDia = (arr) => {
  if (!arr || !arr.length) return null;
  if (arr.every((x) => x === 'si')) return 'todo';
  if (arr.some((x) => x === 'dolor')) return 'parada';
  if (arr.some((x) => x === 'si' || x === 'medias')) return 'parte';
  return 'no';
};

// soloReto=true (portal real del CRM).
//   EL RETO ......... real, de /paciente/mi-reto
//   LA CONSTANCIA ... real desde el 27-ago, de /paciente/pautas/calendario.
//                     Ya NO depende de soloReto: se pinta si hay pauta viva.
//   EL TEST ......... sigue OCULTO: la fecha del próximo test es inventada y
//                     no se le anuncia a nadie un test que no existe. Fase 2.
/* SIN LÁMINA NO SE ESCRIBE «lámina». (Ann, 31-ago-2026: «mira qué horror».)
   El CRM pintaba dentro del hueco la palabra «lámina» y «Lámina "X" ·
   pendiente». Eso es el andamio de la obra, y lo estaba viendo la PACIENTE:
   le decíamos que a su reto le falta material nuestro.
   FisioVisión ya lo tenía resuelto y NO es ninguna de las salidas que se me
   ocurrían: en el prototipo el hueco va VACÍO (`<span class="fv-motiv__vacio">`,
   paciente.html:6347) y el panel grande lleva un iconito neutro de imagen
   (paciente.html:5215). Ni una palabra. Se copia de allí, no se inventa.
   → NORMA_CLONAR_NO_INVENTAR */
const IconoSinLamina = () => (
  <svg className="fv-motiv__sinfoto-ico" width="26" height="26" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="9" cy="10" r="2" />
    <path d="M3 17l5-4 4 3 3-2 6 5" />
  </svg>
);

const MiMotivacionPaciente = ({ endpoint = '/clinica/fisiovision/reto-para-el-paciente', soloReto = false }) => {
  const [reto, setReto] = useState(RETO); // reto en curso · demo hasta que llegue el real
  // El calendario REAL de su pauta. Si no tiene pauta viva el servidor manda
  // {pauta:null} y aquí no se pinta constancia — no un calendario vacío, que
  // también miente: diría «no has hecho nada».
  const [calendario, setCalendario] = useState(null);
  const [calCargado, setCalCargado] = useState(false);
  useEffect(() => {
    axios.get(`${API}/paciente/pautas/calendario`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => setCalendario(r.data && r.data.pauta ? r.data : null))
      .catch(() => setCalendario(null))
      .finally(() => setCalCargado(true));
  }, []);
  const DIARIO = useMemo(() => diarioDeCeldas(calendario), [calendario]);
  const DETALLE = useMemo(() => detalleDeCeldas(calendario), [calendario]);
  const [nivelActual, setNivelActual] = useState(soloReto ? 1 : 2); // 1..4 · nivel 1 = partida (Ann)
  const [cargado, setCargado] = useState(false);   // ¿llegó un reto REAL? (para el estado vacío honesto)
  // ¿YA SÉ si hay reto o no? Distinto de «no hay». Mientras no lo sepa NO se
  // pinta nada del reto: antes se pintaba la plantilla de demostración y, al
  // llegar el real, se sustituía delante de ella. Ann lo describió así:
  // «sale unos segundos la plantilla de base y luego se carga el reto; ese
  // mensaje previo es molesto» (28-ago-2026). Cargando NO es tener contenido.
  const [retoResuelto, setRetoResuelto] = useState(false);

  // Enchufe con el dato REAL. Dos orígenes con shape distinto:
  //  · catálogo admin  (/clinica/fisiovision/reto-para-el-paciente) → { reto: {objetivo, niveles:[{rot,foto}], meta, dia} }
  //  · paciente real   (/paciente/mi-reto, de 4e)                   → { objetivo, niveles:[{rot,imagen_url}], nivel_actual }
  // Se normalizan los dos: la lámina puede venir como `foto` o `imagen_url`.
  useEffect(() => {
    let vivo = true;
    axios.get(`${API}${endpoint}`).then((r) => {
      if (!vivo) return;
      const d = r.data && (r.data.reto || r.data); // el catálogo lo envuelve en .reto; el real es plano
      if (d && d.objetivo) {
        setReto({
          objetivo: d.objetivo,
          niveles: (d.niveles || []).map((n) => ({ rot: n.rot, txt: n.txt || '', foto: n.foto || n.imagen_url || '' })),
          meta: d.meta || (Array.isArray(d.niveles) && d.niveles.length) || 3,
          dia: d.dia != null ? d.dia : 5,
        });
        if (typeof d.nivel_actual === 'number' && d.nivel_actual > 0) setNivelActual(d.nivel_actual);
        setCargado(true);
      }
    }).catch(() => { /* sin dato → estado vacío honesto, nunca la demo */ })
      .finally(() => { if (vivo) setRetoResuelto(true); });
    return () => { vivo = false; };
  }, [endpoint]);
  const [fase, setFase] = useState('reto'); // reto | preguntas | girando | premio
  const [preg, setPreg] = useState(0);
  const [resp, setResp] = useState({});
  const [subida, setSubida] = useState({ antes: 2, destino: 2 });
  const [tiraPos, setTiraPos] = useState(0);   // §3.8.4 · casilla visible de la tragaperras
  const [diaSel, setDiaSel] = useState(HOY.getDate());
  const [mesCal, setMesCal] = useState(HOY.getMonth());
  const [verRueda, setVerRueda] = useState(false); // §4.5 · la rueda de horarios

  const diaTest = reto.dia;
  const esHoyTest = HOY.getDay() === diaTest || HOY.getDay() === (diaTest % 7); // demo: acepta el día
  const faltanDias = (diaTest - HOY.getDay() + 7) % 7;
  const proximo = new Date(HOY); proximo.setDate(HOY.getDate() + faltanDias);

  // ── El Test ── (§3.8 · aviso desactivado: se entra directo a preguntas)
  const abrirTest = () => { setFase('preguntas'); setPreg(0); setResp({}); window.scrollTo(0, 0); };
  const terminarGiro = (destino, respFinal) => {
    const antes = nivelActual;
    setSubida({ antes, destino });
    if (destino > antes) celebrar(destino >= (reto.meta || 4)); // §3.7 celebración solo si sube
    setNivelActual(destino);
    setFase('premio');
    window.__testResp = respFinal; // para mensajeDelSiguiente
  };
  // §3.8.4 · el giro es SOLO teatro: el resultado ya está en lo contestado.
  const girar = (destino, respFinal) => {
    const total = 3 * 4 + (destino - 1);
    let paso = 0;
    const avanza = () => {
      setTiraPos(paso % 4);
      paso += 1;
      if (paso > total) { terminarGiro(destino, respFinal); return; }
      const queda = total - paso;
      setTimeout(avanza, queda > 6 ? 60 : 60 + (7 - queda) * (7 - queda) * 12); // frenada cuadrática
    };
    avanza();
  };
  const responder = (valor) => {
    const p = PREGUNTAS[preg];
    const nuevo = { ...resp, [p.k]: valor };
    setResp(nuevo);
    if (preg < PREGUNTAS.length - 1) { setPreg(preg + 1); return; }
    setFase('girando');
    girar(nuevo.capacidad || 1, nuevo);
  };
  const cerrarPremio = () => { setFase('reto'); window.scrollTo(0, 0); };

  // §3.8.6 · el mensaje de qué viene ahora · 7 reglas, en orden (el dolor manda).
  const mensajeDelSiguiente = (respF, destino) => {
    const d = { No: 1, 'Un poco': 2, Bastante: 3, Mucho: 4 }[respF.dolor] || 1;
    const eMap = { Nada: 1, Poco: 2, Bastante: 3, Muchísimo: 4 };
    const aMap = { 'Sí, todas': 1, 'Casi todas': 2, 'Solo algunas': 3, 'Casi ninguna': 4 };
    const e = eMap[respF.esfuerzo] || 4, a = aMap[respF.pautas] || 4;
    const sig = reto.niveles[Math.min(destino, reto.niveles.length - 1)] || reto.niveles[destino - 1];
    const sigN = (sig && sig.rot) || 'el siguiente nivel';
    if (destino < subida.antes) return { b: 'Lo vamos a mirar.', s: 'Tu fisio ya lo sabe. Entre los dos vemos qué pauta te va mejor.' };
    if (d >= 3) return { b: 'Hoy lo dejamos aquí.', s: 'Ha dolido, y eso manda. Tu fisio lo va a ver.' };
    if (destino >= 4) return { b: 'Lo tienes conseguido.', s: 'Habla con tu fisio: toca elegir un reto nuevo.' };
    if (e <= 2 && a <= 2) return { b: `«${sigN}» está a tu alcance.`, s: 'Ya casi no te cuesta y estás haciendo tus pautas.' };
    if (a >= 3) return { b: 'Vas por buen camino.', s: `Con unas pocas pautas más, «${sigN}» viene solo.` };
    if (e >= 3) return { b: 'Poco a poco.', s: `Todavía te cuesta, y es normal. «${sigN}» llega con las semanas.` };
    return { b: 'Sigue así.', s: `Cada semana estás un poco más cerca de «${sigN}».` };
  };

  const enTest = fase === 'preguntas' || fase === 'girando' || fase === 'premio';

  // ── Calendario del próximo test (mini) ──
  const miniTest = () => {
    // Sin día de test en el dato del reto no hay calendario. Igual que en el
    // prototipo: `if(!dia) return ''`. No se dibuja un mes con una fecha
    // elegida a dedo — sería anunciarle una cita que nadie le ha dado.
    if (!diaTest && diaTest !== 0) return null;
    const y = proximo.getFullYear(), m = proximo.getMonth();
    const primero = new Date(y, m, 1);
    const arranque = (primero.getDay() + 6) % 7;
    const dias = new Date(y, m + 1, 0).getDate();
    const celdas = [];
    for (let i = 0; i < arranque; i++) celdas.push(null);
    for (let dd = 1; dd <= dias; dd++) celdas.push(dd);
    return (
      <div className="fv-calen">
        <div className="fv-calen__rot">Mi próximo test</div>
        <div className="fv-calen__fecha">
          <b>{proximo.getDate()}</b>
          <span>{['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][proximo.getDay()]}<small>{MESES[m]}</small></span>
        </div>
        <div className="fv-calen__mes">{MESES[m]}</div>
        <div className="fv-calen__dias">
          {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((c) => <u key={c}>{c}</u>)}
          {celdas.map((dd, i) => {
            if (dd === null) return <i key={'h' + i} />;
            const fecha = new Date(y, m, dd);
            const esTest = fecha.getDay() === diaTest;
            const esProx = dd === proximo.getDate() && m === proximo.getMonth();
            const esHoy = dd === HOY.getDate() && m === HOY.getMonth() && y === HOY.getFullYear();
            let cls = '';
            if (esProx) cls = 'es-test'; else if (esTest) cls = 'es-otro'; else if (esHoy) cls = 'es-hoy';
            return <i key={dd} className={cls}>{dd}</i>;
          })}
        </div>
      </div>
    );
  };

  // ── El Reto ──
  const elReto = () => (
    <div className="fv-motiv">
      <div className="fv-motiv__izq">
        <div className="crm-portal-bloque-terapeutico">
          <span className="crm-portal-enunciado crm-portal-enunciado--paciente">Mi motivación</span>
          <q>{reto.objetivo}</q>
        </div>
        {/* 4 estados · B/N salvo el actual · hueco marcado (sin láminas) */}
        <div className="fv-motiv__pasos">
          {reto.niveles.map((nv, k) => {
            const num = k + 1;
            const cls = num === nivelActual ? 'es-ahora' : num < nivelActual ? 'es-pasado' : '';
            return (
              <div className={'fv-motiv__paso ' + cls} key={k}>
                {nv.foto ? <img src={nv.foto} alt="" title={nv.rot} /> : <div className="fv-motiv__vacio" title={nv.rot} />}
                <u>{num}</u>
              </div>
            );
          })}
        </div>
        <div className="fv-motiv__texto">
          <div className="fv-motiv__rot">
            Aquí estoy ahora
            <span className="fv-estrellas">{estrellas(nivelActual, reto.meta || reto.niveles.length)}</span>
          </div>
          <b>{reto.niveles[nivelActual - 1].rot}</b>
          <small>{reto.niveles[nivelActual - 1].txt}</small>
        </div>
      </div>

      <div className="fv-motiv__falta">
        <div className="fv-motiv__donde">
          {reto.niveles[nivelActual - 1].foto ? <img className="fv-motiv__foto-img" src={reto.niveles[nivelActual - 1].foto} alt="" /> : <div className="fv-motiv__foto fv-motiv__foto--vacia" title={reto.niveles[nivelActual - 1].rot}><IconoSinLamina /></div>}
          {/* EL PANEL DEL TEST · va SIEMPRE: es lo que equilibra la tarjeta y,
              sobre todo, es la cita que mueve todo esto. Sin ver cuándo toca,
              la foto se queda en un recuerdo bonito.
              El mini calendario solo se pinta si hay día de test DE VERDAD
              (miniTest() devuelve null si no lo hay): no se inventa una fecha. */}
          <div className="fv-motiv__cita">
            {miniTest()}
            <div className={'fv-cita' + (esHoyTest ? ' es-hoy' : '')}>
              <div>
                <b>{esHoyTest ? 'Hoy te toca hacer el test' : 'Tu próximo test'}</b>
                <small>{esHoyTest ? 'Cinco preguntas. ¡Vamos allá!' : `El ${['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][proximo.getDay()]} ${proximo.getDate()} de ${MESES[proximo.getMonth()]}`}</small>
              </div>
              {/* El botón ABRE el test también cuando está «cerrado», igual que en
                  el prototipo: allí los dos botones llaman a abrirTest(). El gris
                  con candado dice «hoy no te toca», no «no puedes» — y así el
                  paciente que quiere adelantarse puede verlo, y nosotros podemos
                  revisarlo. Si algún día tiene que bloquear de verdad, que se
                  decida a propósito, no por un `undefined` heredado. */}
              <button type="button" className={'fv-boton ' + (esHoyTest ? 'fv-boton--accion' : 'fv-boton--cerrado')} onClick={abrirTest}>
                {!esHoyTest && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></svg>
                )}
                Hacer el test
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── El Test · pantalla fv-maquina (§3.8): preguntas · tragaperras · premio ──
  const elTest = () => {
    const cabecera = <div className="fv-bloque-titulo" style={{ marginBottom: 14 }}>Mi test de progreso</div>;

    // FASE 3 · GIRANDO · la tragaperras (§3.8.4)
    if (fase === 'girando') {
      return (
        <div className="fv-maquina">
          {cabecera}
          <div className="fv-maquina__cuenta">Contando lo que has conseguido…</div>
          <div className="fv-maquina__visor">
            <div className="fv-maquina__tira" style={{ transform: `translateY(${-tiraPos * 25}%)` }}>
              {reto.niveles.map((nv, k) => (
                <div className="fv-maquina__casilla" key={k}>
                  {nv.foto ? <img className="fv-maquina__opimg" src={nv.foto} alt="" /> : <div className="fv-maquina__sinfoto" />}
                  <b>{nv.rot}</b>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // FASE 4 · PREMIO (§3.8.5)
    if (fase === 'premio') {
      const respF = (typeof window !== 'undefined' && window.__testResp) || resp;
      const sube = subida.destino > subida.antes;
      const baja = subida.destino < subida.antes;
      const meta = sube && subida.destino >= (reto.meta || 4);
      const nv = reto.niveles[subida.destino - 1] || reto.niveles[0];
      const msg = mensajeDelSiguiente(respF, subida.destino);
      let rot, bien;
      if (meta) { rot = '¡Has conseguido tu reto!'; bien = <>«{reto.objetivo}» Y esto lo has hecho tú.</>; }
      else if (sube) { rot = '¡Bravo! Has mejorado'; bien = <>Esto merece un aplauso. Has subido {subida.destino - subida.antes} nivel{subida.destino - subida.antes === 1 ? '' : 'es'} y eso lo has hecho tú.</>; }
      else if (baja) { rot = 'Hemos retrocedido un poco'; bien = <>Y esto hay que trabajarlo bien. <b>Tu constancia no es el problema.</b> Tu fisio ha recibido un aviso para valorar si alguna pauta puede irte mejor. Te acompañamos para que mejores día a día.</>; }
      else { rot = 'Este es tu resultado de hoy'; bien = null; }
      return (
        <div className={'fv-premio' + (baja ? ' es-atras' : sube ? ' es-mejor' : '')}>
          {!baja && (meta || sube) && <div className="fv-premio__aplauso"><img src="/aplauso.svg" alt="" /></div>}
          <div className="fv-premio__rot">{rot}</div>
          {bien && <p className="fv-premio__bien">{bien}</p>}
          <div className="fv-premio__foto">{nv.foto ? <img src={nv.foto} alt="" style={{ width: '100%', borderRadius: 12 }} /> : <div className="fv-motiv__foto fv-motiv__foto--vacia" style={{ minHeight: 160 }} title={nv.rot}><IconoSinLamina /></div>}</div>
          <div className="fv-premio__nivel">{nv.rot}</div>
          <span className="fv-estrellas">{estrellas(subida.destino, reto.meta || reto.niveles.length)}</span>
          <div className="fv-premio__otras">
            {reto.niveles.map((x, k) => (k === subida.destino - 1 ? null : (
              <div className="fv-premio__otra" key={k}><i /><b>{x.rot}</b></div>
            )))}
          </div>
          <p className="fv-premio__porque">Esto no es suerte: sale de lo que has contestado. Hoy has llegado hasta «{nv.rot}».</p>
          <div className="fv-premio__siguiente"><b>{msg.b}</b><span>{msg.s}</span></div>
          <button type="button" className="fv-boton fv-boton--accion" onClick={cerrarPremio}>Ver mi progreso</button>
        </div>
      );
    }

    // FASE 2 · PREGUNTAS (§3.8.3)
    const p = PREGUNTAS[preg];
    const rotMide = { capacidad: 'Nivel', esfuerzo: 'Esfuerzo', dolor: 'Dolor', pautas: 'Pautas', mejora: 'Mejora' }[p.k];
    return (
      <div className="fv-maquina">
        {cabecera}
        <div className="fv-maquina__cuenta">Pregunta {preg + 1} de {PREGUNTAS.length} · <span>{rotMide}</span></div>
        <div className="fv-maquina__barra">
          {PREGUNTAS.map((_, k) => <i key={k} className={k <= preg ? 'es-hecha' : ''} />)}
        </div>
        <div className="fv-maquina__pregunta">{p.q}</div>
        {p.tipo === 'niveles' ? (
          <div className="fv-maquina__fotos">
            {reto.niveles.map((nv, k) => (
              <button type="button" className="fv-maquina__opfoto" key={k} onClick={() => responder(k + 1)}>
                {/* Aquí se pintaba SIEMPRE el hueco de «sin foto», mirase o no
                    si había lámina. Y en este test la foto no es decoración:
                    el paciente elige EN CUÁL DE LAS CUATRO ESTÁ, y se reconoce
                    en la escena antes que en el rótulo. Sin imagen, el test
                    deja de medir lo que mide. */}
                {nv.foto
                  ? <img className="fv-maquina__opimg" src={nv.foto} alt="" />
                  : <div className="fv-maquina__sinfoto" />}
                <b>{nv.rot}</b>
                <span className="fv-estrellas">{estrellas(k + 1, reto.niveles.length)}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="fv-maquina__ops">
            {p.ops.map((op, k) => (
              <button type="button" className="fv-maquina__op" key={k} onClick={() => responder(op)}>{op}</button>
            ))}
          </div>
        )}
        <small className="fv-maquina__nota">{p.nota || ''}</small>
      </div>
    );
  };

  // ── Mi constancia ──
  const constancia = () => {
    const y = HOY.getFullYear();
    const primero = new Date(y, mesCal, 1);
    const arranque = (primero.getDay() + 6) % 7;
    const dias = new Date(y, mesCal + 1, 0).getDate();
    const celdas = [];
    for (let i = 0; i < arranque; i++) celdas.push(null);
    for (let dd = 1; dd <= dias; dd++) celdas.push(dd);
    while (celdas.length < 7 + 42) celdas.push(null);
    // % del día seleccionado (demo) + racha
    const arrSel = DIARIO[`${y}-${mesCal}-${diaSel}`];
    // §4.6 · cuenta EJERCICIOS, no días: hechos (si) de los que tenía ese día.
    const pct = arrSel ? Math.round(arrSel.filter((x) => x === 'si').length / arrSel.length * 100) : null;
    let racha = 0, mejor = 0;
    for (let dd = 1; dd <= (mesCal === HOY.getMonth() ? HOY.getDate() : dias); dd++) {
      if (resumenDia(DIARIO[`${y}-${mesCal}-${dd}`]) === 'todo') { racha++; mejor = Math.max(mejor, racha); } else racha = 0;
    }
    const esMesActual = mesCal === HOY.getMonth();
    return (
      <div className="fv-cal-dia-wrap">
        <div className="fv-cal-cab">
          <div className="crm-portal-enunciado es-oscuro" style={{ margin: 0 }}>Mi constancia</div>
          <div className="fv-cal-racha">Racha: <b>{racha}</b> días · mejor <b>{mejor}</b></div>
          <button type="button" className={'boton_rueda' + (verRueda ? ' is-on' : '')}
            onClick={() => { setVerRueda((v) => { const nx = !v; if (!nx && esMesActual && diaSel > HOY.getDate()) setDiaSel(HOY.getDate()); return nx; }); }}>
            <svg className="boton_rueda__ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            {verRueda ? 'Ocultar mis horarios' : 'Mis horarios'}
          </button>
        </div>
        <div className="fv-cal-cols">
          <div>
            <div className="fv-cal__nav">
              <button type="button" className="fv-cal__fl" onClick={() => setMesCal((m) => m - 1)}>‹</button>
              <h3 className="fv-cal__mes">{MESES[((mesCal % 12) + 12) % 12]}</h3>
              <button type="button" className={'fv-cal__fl' + (esMesActual ? ' es-off' : '')} onClick={() => !esMesActual && setMesCal((m) => m + 1)}>›</button>
            </div>
            <div className="fv-cal__rejilla">
              {DIAS_SEM.map((c) => <div className="fv-cal__cab" key={c}>{c}</div>)}
              {celdas.slice(0, 42).map((dd, i) => {
                if (dd === null) return <div className="fv-cal__dia fv-cal__hueco" key={'h' + i} />;
                const arrDia = DIARIO[`${y}-${mesCal}-${dd}`];
                const esHoy = dd === HOY.getDate() && esMesActual;
                const futuro = esMesActual && dd > HOY.getDate();
                const plan = futuro && verRueda;         // §4.5 · con la rueda, el futuro es pulsable
                const pulsable = !futuro || plan;
                const cls = 'fv-cal__dia' + (esHoy ? ' es-hoy' : '') + (dd === diaSel ? ' es-elegido' : '') + (futuro && !plan ? ' es-futuro' : '') + (plan ? ' es-plan' : '');
                return (
                  <div className={cls} key={dd} onClick={() => pulsable && setDiaSel(dd)} style={{ cursor: pulsable ? 'pointer' : 'default' }}>
                    {arrDia && !futuro && (
                      <svg className="fv-cal__quesito" viewBox="0 0 32 32">
                        {trozosRosco(arrDia).map((t, k) => <path key={k} className={'fv-qm__t ' + t.cls} d={t.d} />)}
                      </svg>
                    )}
                    {plan && (
                      <svg className="fv-cal__quesito fv-reloj" viewBox="0 0 32 32" fill="none" stroke="#0E8B96" strokeWidth="1.8"><circle cx="16" cy="16" r="10" fill="#fff" /><path d="M16 11v5l3 2" strokeLinecap="round" /></svg>
                    )}
                    <i>{dd}</i>
                  </div>
                );
              })}
            </div>
            {/* EL PORCENTAJE VA AQUÍ · debajo del calendario, no en el panel de
                la derecha. En el prototipo se pinta dentro de #calendario
                (`pintarCumpleDia`): es el resumen de lo que se acaba de mirar
                arriba, y leerlo al lado del día elegido lo desconecta del mes. */}
            {pct !== null && (
              <div className="fv-cumple">
                <div className="fv-cumple__n"><b>{pct}</b><i>%</i></div>
                <div className="fv-cumple__txt">
                  <b>de tu pauta el {['domingo','lunes','martes','miércoles','jueves','viernes','sábado'][new Date(y, mesCal, diaSel).getDay()].replace(/^./, (c) => c.toUpperCase())} {diaSel}</b>
                  <u className="fv-cumple__mes">{MESES[mesCal]}</u>
                </div>
              </div>
            )}
            <div className="fv-cal__clave">
              <span><i className="es-todo" />Todos</span>
              <span><i className="es-parte" />Algunos</span>
              <span><i className="es-parada" />Paré por dolor</span>
              <span><i className="es-nada" />No hice</span>
            </div>
          </div>
          {(() => {
            const ROTULO = { si: 'Hecho', no: 'No se hizo', dolor: 'Incidencia por dolor', medias: 'Lo dejé a medias' };
            const hechosSel = arrSel ? arrSel.filter((x) => x === 'si').length : 0;
            const esHoySel = diaSel === HOY.getDate() && mesCal === HOY.getMonth();
            const diaFecha = new Date(y, mesCal, diaSel);
            const dTxt = `${esHoySel ? 'Hoy · ' : ''}${['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][diaFecha.getDay()]}, ${diaSel} de ${MESES[mesCal]}`;
            // §4.6quater · día futuro con la rueda → LO QUE TE TOCA (no lo hecho)
            const esFuturoSel = esMesActual && diaSel > HOY.getDate();
            if (esFuturoSel && verRueda) {
              return (
                <div className="fv-dsel es-plan">
                  <div className="fv-dsel__dia">{dTxt}</div>
                  {(() => {
                    const iso = `${y}-${String(mesCal + 1).padStart(2, '0')}-${String(diaSel).padStart(2, '0')}`;
                    const plan = planDeFecha(calendario, iso);
                    if (!plan.length) return <div className="fv-plan__pie">Ese día no te toca pauta. Descansa.</div>;
                    return plan.map((pl, k) => (
                      <div className="fv-plan__fila" key={k}>
                        <span>{pl.mom}<i>{pl.n} {pl.n === 1 ? 'ejercicio' : 'ejercicios'}</i></span>
                      </div>
                    ));
                  })()}
                </div>
              );
            }
            const dolorDia = arrSel && arrSel.includes('dolor');
            return (
              <div className="fv-dsel">
                <div className="fv-dsel__dia">{dTxt}</div>
                {arrSel ? (
                  <>
                    <div className="fv-dsel__cifras">
                      <span><b>{hechosSel}</b> de {arrSel.length}</span>
                      {dolorDia && <span className="es-dolorhoy es-bastante">hubo <b>dolor</b></span>}
                    </div>
                    {arrSel.map((est, k) => {
                      const info = (DETALLE[`${y}-${mesCal}-${diaSel}`] || [])[k] || {};
                      return (
                      <div className={'fv-dsel__fila es-' + est} title={ROTULO[est]} key={k}>
                        <svg className={'fv-trozo es-' + est} viewBox="0 0 20 20"><path d={trozoSuelto(arrSel.length)} /></svg>
                        {/* su lámina · si no la hay, el hueco; pero no el hueco SIEMPRE */}
                        {info.foto
                          ? <img className="fv-dsel__foto" src={info.foto} alt="" />
                          : <span className="fv-dsel__sinfoto" />}
                        <b>{info.titulo || `Ejercicio ${k + 1}`}</b>
                      </div>
                      );
                    })}
                    <div className="fv-dsel__clave">
                      <span><svg className="fv-trozo es-si" viewBox="0 0 20 20"><path d={trozoSuelto(1)} /></svg>Hecho</span>
                      <span><svg className="fv-trozo es-medias" viewBox="0 0 20 20"><path d={trozoSuelto(1)} /></svg>A medias</span>
                      <span><svg className="fv-trozo es-dolor" viewBox="0 0 20 20"><path d={trozoSuelto(1)} /></svg>Dolor</span>
                      <span><svg className="fv-trozo es-no" viewBox="0 0 20 20"><path d={trozoSuelto(1)} /></svg>No se hizo</span>
                    </div>
                  </>
                ) : <p style={{ color: '#93A0B5', fontSize: 14 }}>Sin dato ese día.</p>}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  return (
    <div className="fvpac" style={{ padding: 0, maxWidth: 'none', background: 'transparent' }}>
      <div className="fvpac__titular"><h1>Mi motivación · Reto</h1></div>
      {enTest ? elTest() : (
        <>
          {/* soloReto: el RETO es real. La constancia y el test se muestran como
              ESQUELETO con dato DEMO (racha/dolor/fecha inventados) → sirven para
              diseñar, pero NO pueden ir a producción hasta enchufar dato real. */}
          {!retoResuelto ? null : soloReto && !cargado ? (
            <div className="crm-portal-bloque-terapeutico" style={{ marginTop: 8 }}>
              <p style={{ color: '#5B7B84', margin: 0, lineHeight: 1.5 }}>
                Tu fisio todavía no te ha puesto ningún reto. Te avisará cuando tengas el primero.
              </p>
            </div>
          ) : elReto()}
          {/* CONSTANCIA · ya con dato REAL (/paciente/pautas/calendario).
              Se pinta solo si hay pauta viva: sin ella no se enseña un
              calendario vacío, que diría «no has hecho nada» y sería mentira. */}
          {calCargado && calendario && (
            <div style={{ marginTop: 22 }} className="fv-sf">{constancia()}</div>
          )}
        </>
      )}
    </div>
  );
};

export default MiMotivacionPaciente;
